import { Link } from "react-router-dom";
import { Phone, Mail, Facebook } from "lucide-react";
import { NAV_LINKS, SITE } from "@/siteConfig";
import { useContent } from "@/content/ContentContext";
import { SUBURBS_DETAILED } from "@/data/suburbs";

export default function Footer() {
  const { get } = useContent();
  return (
    <footer data-testid="site-footer" className="bg-charcoal text-ivory">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <img src="/images/logo-white.png" alt="Modify — We Care" data-testid="footer-logo" className="w-44" />
            <p className="mt-6 max-w-sm text-base leading-relaxed text-ivory/70">
              Grab rails, hand rails and rubber ramps — supplied and professionally installed across Perth, Western
              Australia.
            </p>
            <p className="mt-4 font-display text-lg font-bold text-sage-tint">We care about making everyday living safer.</p>
          </div>

          <nav aria-label="Footer" className="md:col-span-2">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">Explore</h2>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className="inline-flex min-h-[32px] items-center text-sm font-semibold text-ivory/75 transition-colors duration-200 hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Service areas" className="md:col-span-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">Service Areas</h2>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3">
              {SUBURBS_DETAILED.map((suburb) => (
                <li key={suburb.slug}>
                  <Link
                    to={`/areas/${suburb.slug}`}
                    data-testid={`footer-area-${suburb.slug}-link`}
                    className="inline-flex min-h-[28px] items-center text-xs font-semibold text-ivory/60 transition-colors duration-200 hover:text-white"
                  >
                    {suburb.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">Contact</h2>
            <ul className="mt-5 space-y-4">
              <li>
                <a
                  href={get("site_phone_href", SITE.phoneHref)}
                  data-testid="footer-phone-link"
                  className="inline-flex min-h-[40px] items-center gap-3 text-base font-bold text-ivory transition-colors duration-200 hover:text-sage-tint"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {get("site_phone_display", SITE.phoneDisplay)}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${get("site_email", SITE.email)}`}
                  data-testid="footer-email-link"
                  className="inline-flex min-h-[40px] min-w-0 items-center gap-3 break-all text-base font-semibold text-ivory/75 transition-colors duration-200 hover:text-white"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {get("site_email", SITE.email)}
                </a>
              </li>
              <li>
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook-link"
                  className="inline-flex min-h-[40px] items-center gap-3 text-base font-semibold text-ivory/75 transition-colors duration-200 hover:text-white"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                  Find us on Facebook
                </a>
              </li>
            </ul>
            <p className="mt-6 text-sm text-ivory/50">Servicing the Perth metro area, WA.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-sm text-ivory/50">© Modify — All Rights Reserved</p>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sage-tint">We Care</p>
        </div>
      </div>
    </footer>
  );
}
